import {
  BrowserProvider,
  Contract,
  formatUnits,
  JsonRpcProvider,
  parseUnits,
} from "ethers";

import { useCallback, useState } from "react";

import {
  STAKING_CONTRACT_ADDRESS,
  STAKING_ABI,
  TOKEN_CONTRACT_ADDRESS,
  TOKEN_ABI,
} from "../contracts/contracts";

// =========================================================
// MANUAL STAKING PLANS
// =========================================================

const manualPlans = [
  {
    id: 0,
    lockDays: 90,
    monthlyRateBps: 1000,
  },
  {
    id: 1,
    lockDays: 90,
    monthlyRateBps: 1500,
  },
  {
    id: 2,
    lockDays: 90,
    monthlyRateBps: 2000,
  },
  {
    id: 3,
    lockDays: 90,
    monthlyRateBps: 2000,
  },
];

// =========================================================
// RPC
// =========================================================

const CUSTOM_RPC =
  "https://bsc-dataseed.bnbchain.org";

// =========================================================
// HOOK
// =========================================================

function useContract() {
  const [address, setAddress] = useState(null);
  const [isConnected, setIsConnected] = useState(false);

  // =========================================================
  // BITGET / WALLET PROVIDER
  // =========================================================

  const getEthereum = useCallback(() => {
    if (typeof window === "undefined") {
      return null;
    }

    /*
     * Bitget Wallet browser extension
     *
     * Prefer Bitget's own injected provider.
     */
    if (window.bitkeep?.ethereum) {
      return window.bitkeep.ethereum;
    }

    /*
     * Fallback for other injected EVM wallets.
     */
    if (window.ethereum) {
      return window.ethereum;
    }

    return null;
  }, []);

  // =========================================================
  // CONNECT WALLET
  // =========================================================

  const connectWallet = useCallback(async () => {
    try {
      const ethereum = getEthereum();

      if (!ethereum) {
        throw new Error(
          "No Web3 wallet detected. Please install Bitget Wallet or another compatible wallet."
        );
      }

      const accounts = await ethereum.request({
        method: "eth_requestAccounts",
      });

      if (accounts && accounts.length > 0) {
        setAddress(accounts[0]);
        setIsConnected(true);

        return accounts[0];
      }

      setAddress(null);
      setIsConnected(false);

      return null;
    } catch (err) {
      console.error("connectWallet error:", err);
      throw err;
    }
  }, [getEthereum]);

  // =========================================================
  // DISCONNECT WALLET
  // =========================================================

  const disconnectWallet = useCallback(() => {
    setAddress(null);
    setIsConnected(false);
  }, []);

  // =========================================================
  // CHECK WALLET CONNECTION
  // =========================================================

  const checkWalletConnection = useCallback(async () => {
    try {
      const ethereum = getEthereum();

      if (!ethereum) {
        setAddress(null);
        setIsConnected(false);
        return null;
      }

      const accounts = await ethereum.request({
        method: "eth_accounts",
      });

      if (accounts && accounts.length > 0) {
        setAddress(accounts[0]);
        setIsConnected(true);

        return accounts[0];
      }

      setAddress(null);
      setIsConnected(false);

      return null;
    } catch (err) {
      console.error(
        "checkWalletConnection error:",
        err
      );

      setAddress(null);
      setIsConnected(false);

      return null;
    }
  }, [getEthereum]);

  // =========================================================
  // WALLET PROVIDER
  // =========================================================

  const getProvider = useCallback(() => {
    const ethereum = getEthereum();

    if (!ethereum) {
      throw new Error(
        "Wallet not found. Please install Bitget Wallet."
      );
    }

    return new BrowserProvider(ethereum);
  }, [getEthereum]);

  // =========================================================
  // SIGNER
  // =========================================================

  const getSigner = useCallback(async () => {
    const provider = getProvider();

    return await provider.getSigner();
  }, [getProvider]);

  // =========================================================
  // CONTRACT
  // =========================================================

  const getContract = useCallback(
    async (contractAddress, abi) => {
      const signer = await getSigner();

      return new Contract(
        contractAddress,
        abi,
        signer
      );
    },
    [getSigner]
  );

  // =========================================================
  // READ-ONLY PROVIDER
  // =========================================================

  const getReadProvider = useCallback(() => {
    return new JsonRpcProvider(CUSTOM_RPC);
  }, []);

  // =========================================================
  // READ-ONLY STAKING CONTRACT
  // =========================================================

  const getReadStakingContract =
    useCallback(() => {
      const provider = getReadProvider();

      return new Contract(
        STAKING_CONTRACT_ADDRESS,
        STAKING_ABI,
        provider
      );
    }, [getReadProvider]);

  // =========================================================
  // READ-ONLY TOKEN CONTRACT
  // =========================================================

  const getReadTokenContract =
    useCallback(() => {
      const provider = getReadProvider();

      return new Contract(
        TOKEN_CONTRACT_ADDRESS,
        TOKEN_ABI,
        provider
      );
    }, [getReadProvider]);

  // =========================================================
  // USER STAKING POSITIONS
  // =========================================================

  const getUserPositions = useCallback(
    async () => {
      if (!address) {
        return {
          positions: [],
          personalStake: 0,
          totalBabyEarnings: 0,
          totalUSDEarnings: 0,
        };
      }

      try {
        const contract =
          getReadStakingContract();

        const positionIds =
          await contract.positionsOf(address);

        const positions = [];

        let personalStake = 0;
        let totalBabyEarnings = 0;
        let totalUSDEarnings = 0;

        for (
          let i = 0;
          i < positionIds.length;
          i++
        ) {
          const id = positionIds[i];

          const pos =
            await contract.positionInfo(id);

          const planId =
            Number(pos[0].planId);

          const plan =
            manualPlans.find(
              (p) => p.id === planId
            ) || {
              lockDays: 0,
              monthlyRateBps: 0,
            };

          const stakeAmountNum =
            Number(
              formatUnits(
                pos[0].stakeAmount,
                18
              )
            );

          const fixedReturns = (
            stakeAmountNum *
            (plan.monthlyRateBps / 10000) *
            (plan.lockDays / 30)
          ).toFixed(2);

          const claimableBaby =
            Number(
              formatUnits(
                pos[2],
                18
              )
            );

          const claimableUSD =
            Number(
              formatUnits(
                pos[1],
                18
              )
            );

          personalStake +=
            stakeAmountNum;

          totalBabyEarnings +=
            claimableBaby +
            Number(pos[0].paidUSD) /
              (plan.monthlyRateBps || 1);

          totalUSDEarnings +=
            claimableUSD +
            Number(pos[0].paidUSD);

          const startTime =
            Number(pos[0].startTime);

          const endTime =
            Number(pos[0].endTime);

          positions.push({
            id,

            user:
              pos[0].user,

            vault:
              pos[0].vault,

            planId,

            lockDays:
              plan.lockDays,

            stakeAmount:
              formatUnits(
                pos[0].stakeAmount,
                18
              ),

            stakeUSD:
              formatUnits(
                pos[0].stakeUSD,
                18
              ),

            fixedReturns,

            startTime,

            endTime,

            startTimeFormatted:
              new Date(
                startTime * 1000
              ).toLocaleString(),

            endTimeFormatted:
              new Date(
                endTime * 1000
              ).toLocaleString(),

            maxUSDReward:
              formatUnits(
                pos[0].maxUSDReward,
                18
              ),

            paidUSD:
              formatUnits(
                pos[0].paidUSD,
                18
              ),

            closed:
              pos[0].closed,

            claimableUSD:
              formatUnits(
                pos[1],
                18
              ),

            claimableBaby:
              formatUnits(
                pos[2],
                18
              ),
          });
        }

        return {
          positions,
          personalStake,
          totalBabyEarnings,
          totalUSDEarnings,
        };
      } catch (e) {
        console.error(
          "Error fetching user positions:",
          e
        );

        return {
          positions: [],
          personalStake: 0,
          totalBabyEarnings: 0,
          totalUSDEarnings: 0,
        };
      }
    },
    [address, getReadStakingContract]
  );

  // =========================================================
  // REFERRAL EARNINGS
  // =========================================================

  const getReferralEarnings =
    useCallback(async () => {
      if (!address) {
        return "0";
      }

      try {
        const contract =
          getReadStakingContract();

        const earnings =
          await contract.referralEarningsOf(
            address
          );

        return formatUnits(
          earnings,
          18
        );
      } catch (e) {
        console.error(
          "Error fetching referral earnings:",
          e
        );

        return "0";
      }
    }, [
      address,
      getReadStakingContract,
    ]);

  // =========================================================
  // STAKE TOKENS
  // =========================================================

  const stakeTokens = useCallback(
    async (
      amount,
      planId,
      referrer
    ) => {
      if (!isConnected) {
        throw new Error(
          "Wallet not connected"
        );
      }

      const token =
        await getContract(
          TOKEN_CONTRACT_ADDRESS,
          TOKEN_ABI
        );

      const stakingContract =
        await getContract(
          STAKING_CONTRACT_ADDRESS,
          STAKING_ABI
        );

      const parsedAmount =
        parseUnits(
          amount.toString(),
          18
        );

      /*
       * APPROVAL
       */
      const approvalTx =
        await token.approve(
          STAKING_CONTRACT_ADDRESS,
          parsedAmount
        );

      await approvalTx.wait();

      /*
       * STAKE
       */
      const stakeTx =
        await stakingContract.stake(
          parsedAmount,
          planId,
          referrer
        );

      /*
       * Return immediately after wallet
       * accepts the transaction.
       */
      return stakeTx.hash;
    },
    [isConnected, getContract]
  );

  // =========================================================
  // CLAIM POSITION
  // =========================================================

  const claimPosition = useCallback(
    async (positionId) => {
      if (!isConnected) {
        throw new Error(
          "Wallet not connected"
        );
      }

      const stakingContract =
        await getContract(
          STAKING_CONTRACT_ADDRESS,
          STAKING_ABI
        );

      console.log(
        "Opening Bitget claim confirmation..."
      );

      const tx =
        await stakingContract.claim(
          positionId
        );

      console.log(
        "Claim submitted:",
        tx.hash
      );

      return tx.hash;
    },
    [isConnected, getContract]
  );

  // =========================================================
  // UNSTAKE POSITION
  // =========================================================

  const unstakePosition =
    useCallback(
      async (positionId) => {
        if (!isConnected) {
          throw new Error(
            "Wallet not connected"
          );
        }

        const stakingContract =
          await getContract(
            STAKING_CONTRACT_ADDRESS,
            STAKING_ABI
          );

        console.log(
          "Opening Bitget unstake confirmation..."
        );

        const tx =
          await stakingContract.unstake(
            positionId
          );

        console.log(
          "Unstake submitted:",
          tx.hash
        );

        return tx.hash;
      },
      [isConnected, getContract]
    );

  // =========================================================
  // GLOBAL STATS
  // =========================================================

  const getGlobalStats =
    useCallback(async () => {
      try {
        const contract =
          getReadStakingContract();

        console.log(
          "→ Fetching totalStakedKIND"
        );

        const totalPoolStake =
          await contract.totalStakedKIND();

        console.log(
          "→ Fetching totalClaimedGlobal"
        );

        const [
          globalClaimedUSD,
          globalClaimedBaby,
        ] =
          await contract.totalClaimedGlobal();

        console.log(
          "→ Fetching claimableRewardsGlobal"
        );

        const [
          globalClaimableUSD,
          globalClaimableBaby,
        ] =
          await contract.totalClaimableGlobal();

        console.log(
          "→ Fetching positionCounter"
        );

        const positionCounter =
          await contract.positionCounter();

        return {
          totalPoolStake:
            Number(
              formatUnits(
                totalPoolStake,
                18
              )
            ),

          globalClaimedUSD:
            Number(
              formatUnits(
                globalClaimedUSD,
                18
              )
            ),

          globalClaimedBaby:
            Number(
              formatUnits(
                globalClaimedBaby,
                18
              )
            ),

          globalClaimableUSD:
            Number(
              formatUnits(
                globalClaimableUSD,
                18
              )
            ),

          globalClaimableBaby:
            Number(
              formatUnits(
                globalClaimableBaby,
                18
              )
            ),

          positionCounter:
            Number(positionCounter),
        };
      } catch (e) {
        console.error(
          "Error fetching global stats:",
          e
        );

        return null;
      }
    }, [getReadStakingContract]);

  // =========================================================
  // USER TOTAL CLAIMED
  // =========================================================

  const getUserTotalClaimed =
    useCallback(
      async (userAddress) => {
        if (!userAddress) {
          return {
            totalUSD: 0,
            totalHUG: 0,
          };
        }

        try {
          const contract =
            getReadStakingContract();

          const [
            totalUSDRaw,
            totalHUGRaw,
          ] =
            await contract.totalClaimedByUser(
              userAddress
            );

          return {
            totalUSD:
              Number(
                formatUnits(
                  totalUSDRaw || 0,
                  18
                )
              ),

            totalHUG:
              Number(
                formatUnits(
                  totalHUGRaw || 0,
                  18
                )
              ),
          };
        } catch (err) {
          console.error(
            "Error fetching total claimed by user:",
            err
          );

          return {
            totalUSD: 0,
            totalHUG: 0,
          };
        }
      },
      [getReadStakingContract]
    );

  // =========================================================
  // USER TOKEN BALANCE
  // =========================================================

  const getUserTokenBalance =
    useCallback(
      async (userAddress) => {
        if (!userAddress) {
          return "0";
        }

        try {
          const tokenContract =
            getReadTokenContract();

          const balanceRaw =
            await tokenContract.balanceOf(
              userAddress
            );

          return Number(
            formatUnits(
              balanceRaw,
              18
            )
          );
        } catch (err) {
          console.error(
            "Error fetching user token balance:",
            err
          );

          return 0;
        }
      },
      [getReadTokenContract]
    );

  // =========================================================
  // USER CLAIMABLE REWARDS
  // =========================================================

  const getUserClaimableRewards =
    useCallback(
      async (userAddress) => {
        if (!userAddress) {
          return {
            claimableUSD: 0,
            claimableHUG: 0,
          };
        }

        try {
          const contract =
            getReadStakingContract();

          const [
            claimableUSD,
            claimableHUG,
          ] =
            await contract.claimableRewardsOf(
              userAddress
            );

          return {
            claimableUSD:
              Number(
                formatUnits(
                  claimableUSD,
                  18
                )
              ),

            claimableHUG:
              Number(
                formatUnits(
                  claimableHUG,
                  18
                )
              ),
          };
        } catch (e) {
          console.error(
            "Error fetching user claimable rewards:",
            e
          );

          return {
            claimableUSD: 0,
            claimableHUG: 0,
          };
        }
      },
      [getReadStakingContract]
    );

  // =========================================================
  // POSITION COUNTER
  // =========================================================

  const getPositionCounter =
    useCallback(async () => {
      try {
        const contract =
          getReadStakingContract();

        console.log(
          "→ Fetching total staking positions"
        );

        const totalPositions =
          await contract.positionCounter();

        return Number(
          totalPositions
        );
      } catch (err) {
        console.error(
          "Error fetching positionCounter:",
          err
        );

        return 0;
      }
    }, [getReadStakingContract]);

  // =========================================================
  // CLAIM KIND REWARDS
  // =========================================================

  const claimKindRewards =
    useCallback(async () => {
      if (!isConnected) {
        throw new Error(
          "Wallet not connected"
        );
      }

      try {
        const stakingContract =
          await getContract(
            STAKING_CONTRACT_ADDRESS,
            STAKING_ABI
          );

        console.log(
          "Opening Bitget KIND reward confirmation..."
        );

        const tx =
          await stakingContract.claimKindRewards();

        console.log(
          "KIND rewards submitted:",
          tx.hash
        );

        return tx.hash;
      } catch (e) {
        console.error(
          "Error claiming KIND rewards:",
          e
        );

        throw e;
      }
    }, [isConnected, getContract]);

  // =========================================================
  // PENDING KIND REWARDS
  // =========================================================

  const getPendingKindRewards =
    useCallback(
      async (userAddress) => {
        if (!userAddress) {
          return 0;
        }

        try {
          const contract =
            getReadStakingContract();

          const pendingRaw =
            await contract.pendingKindRewards(
              userAddress
            );

          return Number(
            formatUnits(
              pendingRaw,
              18
            )
          );
        } catch (e) {
          console.error(
            "Error fetching pending KIND rewards:",
            e
          );

          return 0;
        }
      },
      [getReadStakingContract]
    );

  // =========================================================
  // TOTAL KIND CLAIMED
  // =========================================================

  const getTotalKindClaimed =
    useCallback(
      async (userAddress) => {
        if (!userAddress) {
          return 0;
        }

        try {
          const contract =
            getReadStakingContract();

          const claimedRaw =
            await contract.totalKindClaimed(
              userAddress
            );

          return Number(
            formatUnits(
              claimedRaw,
              18
            )
          );
        } catch (e) {
          console.error(
            "Error fetching claimed KIND:",
            e
          );

          return 0;
        }
      },
      [getReadStakingContract]
    );

  // =========================================================
  // RETURN
  // =========================================================

  return {
    // -------------------------
    // WALLET
    // -------------------------

    address,
    isConnected,
    connectWallet,
    disconnectWallet,
    checkWalletConnection,

    // -------------------------
    // USER POSITIONS
    // -------------------------

    getUserPositions,
    getReferralEarnings,

    // -------------------------
    // TRANSACTIONS
    // -------------------------

    stakeTokens,
    claimPosition,
    unstakePosition,
    claimKindRewards,

    // -------------------------
    // READS
    // -------------------------

    getGlobalStats,
    getUserTotalClaimed,
    getUserTokenBalance,
    getUserClaimableRewards,
    getPositionCounter,

    // -------------------------
    // KIND REWARDS
    // -------------------------

    getPendingKindRewards,
    getTotalKindClaimed,
  };
}

export default useContract;